import { Head, Link } from "@inertiajs/react"
import { ArrowLeft } from "lucide-react"
import { useTranslation } from "react-i18next"

import { AddCandidateSheet } from "@/components/candidates/add-candidate-sheet"
import { useCurrentContext } from "@/hooks/use-current-context"
import AppLayout from "@/layouts/app-layout"
import { candidatesPath } from "@/routes"
import type { CandidateFormState } from "@/types/candidates"

interface CandidatesNewPageProps {
  candidate: CandidateFormState
}

/**
 * pages/Brand/Candidates/New — standalone single-add fallback.
 *
 * The primary single-add entry point is the URL-state AddCandidateSheet
 * mounted on `Brand/Candidates/Index.tsx` (OKS-629). This standalone
 * page exists so:
 *   1. Direct navigation to `/candidates/new` works (backend's
 *      `Candidates/New` Inertia render needs a page component).
 *   2. The PRG redirect target on validation failure
 *      (`redirect_back fallback_location: new_candidate_path`)
 *      lands on a page when no Referer is available (rare edge
 *      case; e.g. external bookmark).
 *
 * Implementation: open the Sheet immediately on mount, with the brand
 * "back to candidates" link as the page chrome behind. The user sees
 * the same surface as the index Sheet flow — consistency over
 * duplication. Close → `history.back()` (URL state lives on the index,
 * not here, so there's no `?add` param to clear on this route).
 *
 * **Echo path:** backend ships an empty `candidate` prop initially and
 * the typed values on validation-failure re-render. The Sheet's
 * `defaultValues` accepts that shape; `approx_followers` `number | null`
 * is coerced to a string (`null → ""`) before passing in.
 */
export default function CandidatesNew({ candidate }: CandidatesNewPageProps) {
  const { t } = useTranslation()
  const { currentBrand } = useCurrentContext()
  const brandName = currentBrand?.name ?? ""

  return (
    <AppLayout>
      <Head title={t("candidates.add.head_title")} />
      <div className="flex flex-col gap-6 p-6">
        <header className="flex flex-col gap-3">
          <Link
            href={candidatesPath()}
            className="text-muted-foreground hover:text-foreground inline-flex w-fit items-center gap-1 text-sm"
            data-testid="candidates-new-back-link"
          >
            <ArrowLeft className="size-4" aria-hidden="true" />
            {t("candidates.bulk.back_to_candidates")}
          </Link>
          <h1 className="text-foreground text-2xl font-semibold tracking-tight">
            {t("candidates.add.head_title")}
          </h1>
        </header>

        <AddCandidateSheet
          // Standalone page: Sheet opens immediately on mount.
          open
          onOpenChange={(next) => {
            // Close → back to the index. Preserves the "add or
            // browse" muscle memory; alternative would be staying
            // on a blank page.
            if (!next) window.history.back()
          }}
          brandName={brandName}
          defaultValues={{
            tiktok_url: candidate.tiktok_url,
            display_name: candidate.display_name,
            // OKS-601 — echo the contact email so a validation-failure
            // round-trip preserves the typed value.
            email: candidate.email,
            approx_followers:
              candidate.approx_followers === null
                ? ""
                : String(candidate.approx_followers),
            bio: candidate.bio,
            notes: candidate.notes,
            tags: candidate.tags,
            country: candidate.country,
            city: candidate.city,
          }}
        />
      </div>
    </AppLayout>
  )
}
